import { useEffect, useRef, useState } from 'react'

/**
 * Returns a ref to attach to a wrapper and the vertical offset (px) that keeps
 * its media drifting against the page scroll.
 *
 * @param strength Fraction of the element's travel to offset. 0.25 = clearly visible depth.
 */
export function useParallax<T extends HTMLElement>(strength = 0.25) {
  const ref = useRef<T>(null)
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let frame = 0
    const update = () => {
      const el = ref.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      // Distance of the element's centre from the viewport centre
      const delta = rect.top + rect.height / 2 - window.innerHeight / 2
      setOffset(-delta * strength)
    }

    const onScroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [strength])

  return { ref, offset }
}
